/**
 * B2B Cart Gate — assets/b2b-cart-gate.js
 *
 * Funktioniert sowohl auf der Cart-Seite (main-cart-footer)
 * als auch im Cart Drawer (cart-drawer.liquid).
 *
 * Besonderheiten Dawn:
 *  - Cart-Seite: Dawn ersetzt `.js-contents` via Section Rendering API
 *    nach Mengenänderungen → Gate muss sich nach DOM-Updates neu initialisieren.
 *  - Cart Drawer: `CartDrawer.renderContents()` tauscht `#CartDrawer` komplett aus
 *    → gleiche Anforderung.
 *  - Lösung: MutationObserver auf beide Container + DOMContentLoaded.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'b2b_confirmed';
  const SELECTOR_GATE = '[data-b2b-gate]';
  const SELECTOR_CHECKBOX = '[data-b2b-checkbox]';
  const SELECTOR_CHECKOUT = '[data-b2b-checkout]';
  const SELECTOR_DYNAMIC = '[data-b2b-dynamic-checkout]';

  /** Liest den gespeicherten Zustand aus sessionStorage. */
  function getSavedState() {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  }

  /** Speichert den Zustand in sessionStorage (bleibt bei Page-Reload erhalten). */
  function saveState(checked) {
    sessionStorage.setItem(STORAGE_KEY, checked ? '1' : '0');
  }

  /**
   * Schreibt die B2B-Bestätigung als Cart Attribute — erscheint dann
   * in jeder Order im Shopify Admin und ist rechtlich dokumentiert.
   */
  function updateCartAttribute(confirmed) {
    const value = confirmed
      ? 'Ja – bestätigt am ' + new Date().toLocaleString('de-DE')
      : '';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'B2B-Bestätigung': value,
          'B2B-Rechtsgrundlage': confirmed ? '§14 BGB' : '',
        },
      }),
    }).catch(function (err) {
      console.warn('[B2B Gate] Cart attribute update fehlgeschlagen:', err);
    });
  }

  /**
   * Aktiviert / deaktiviert alle Checkout-Elemente im Scope eines Gates.
   * `scope` ist das Element, in dem gesucht wird (form oder document).
   */
  function applyState(scope, checked) {
    // Standard Checkout-Button(s)
    scope.querySelectorAll(SELECTOR_CHECKOUT).forEach(function (btn) {
      btn.disabled = !checked;
      btn.setAttribute('aria-disabled', String(!checked));
    });

    // Dynamic Checkout (Shop Pay, PayPal, Google Pay …)
    scope.querySelectorAll(SELECTOR_DYNAMIC).forEach(function (wrapper) {
      wrapper.style.opacity = checked ? '' : '0.35';
      wrapper.style.pointerEvents = checked ? '' : 'none';
      wrapper.setAttribute('aria-hidden', String(!checked));
    });

    // Fehlertext + CSS-Klassen
    const gate =
      scope.querySelector(SELECTOR_GATE) || scope.closest(SELECTOR_GATE);
    if (gate) {
      const errorEl = gate.querySelector('.b2b-gate__error');
      if (errorEl) {
        errorEl.hidden = checked;
      }
      gate.classList.toggle('b2b-gate--active', checked);
      gate.classList.toggle('b2b-gate--inactive', !checked);
    }
  }

  /**
   * Initialisiert ein einzelnes Gate-Element.
   * `gateEl` = das [data-b2b-gate]-Element.
   */
  function initGate(gateEl) {
    if (gateEl._b2bInit) return; // Mehrfach-Init verhindern
    gateEl._b2bInit = true;

    const checkbox = gateEl.querySelector(SELECTOR_CHECKBOX);
    if (!checkbox) return;

    // Scope: das umschließende <form> oder der Drawer-Footer
    const scope =
      gateEl.closest('form') ||
      gateEl.closest('.cart-drawer__footer') ||
      document;

    // Gespeicherten Zustand wiederherstellen
    const saved = getSavedState();
    checkbox.checked = saved;
    applyState(scope, saved);

    checkbox.addEventListener('change', function () {
      saveState(checkbox.checked);
      applyState(scope, checkbox.checked);
      updateCartAttribute(checkbox.checked);

      // Fehlermeldung erst anzeigen wenn Nutzer aktiv interagiert hat
      if (!checkbox.checked) {
        const errorEl = gateEl.querySelector('.b2b-gate__error');
        if (errorEl) errorEl.hidden = false;
      }
    });
  }

  /** Sucht und initialisiert alle Gates im Dokument. */
  function initAll() {
    document.querySelectorAll(SELECTOR_GATE).forEach(function (gateEl) {
      // Flag zurücksetzen wenn das Element frisch aus dem DOM kommt
      gateEl._b2bInit = false;
      initGate(gateEl);
    });
  }

  /**
   * Submit-Interceptor — fängt jeden Checkout-Versuch ab,
   * auch wenn jemand /checkout direkt aufruft oder einen alten Link nutzt.
   * Leitet zur Cart-Seite weiter und scrollt das Gate ins Bild.
   */
  function initSubmitInterceptor() {
    document.addEventListener('submit', function (e) {
      const form = e.target;
      const isCheckoutForm =
        form.getAttribute('action') === '/checkout' ||
        !!form.querySelector('[name="checkout"]');

      if (!isCheckoutForm) return;

      const confirmed = getSavedState();
      if (!confirmed) {
        e.preventDefault();
        window.location.href = '/cart?b2b_required=1';
      }
    });
  }

  /**
   * Hebt das Gate visuell hervor wenn der Nutzer via ?b2b_required=1
   * auf die Cart-Seite weitergeleitet wurde.
   */
  function handleB2BRequiredParam() {
    if (!window.location.search.includes('b2b_required=1')) return;

    // URL bereinigen ohne Reload
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    // Warten bis DOM und Gate bereit sind
    setTimeout(function () {
      const gate = document.querySelector(SELECTOR_GATE);
      if (!gate) return;
      gate.scrollIntoView({ behavior: 'smooth', block: 'center' });
      gate.classList.add('b2b-gate--highlight');
      setTimeout(function () {
        gate.classList.remove('b2b-gate--highlight');
      }, 2500);
    }, 300);
  }

  /**
   * MutationObserver — reagiert auf DOM-Änderungen durch Dawn's
   * Section Rendering API (Cart-Seite) und CartDrawer.renderContents() (Drawer).
   */
  function observeDawnUpdates() {
    const targets = [
      document.getElementById('main-cart-footer'),
      document.getElementById('CartDrawer'),
    ].filter(Boolean);

    if (!targets.length) return;

    const observer = new MutationObserver(function (mutations) {
      const relevant = mutations.some(function (m) {
        return m.type === 'childList' && m.addedNodes.length > 0;
      });
      if (relevant) initAll();
    });

    targets.forEach(function (target) {
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  // ── Initialisierung ───────────────────────────────────────
  function boot() {
    initAll();
    observeDawnUpdates();
    initSubmitInterceptor();
    handleB2BRequiredParam();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Fallback: Dawn PubSub-Events (falls vorhanden)
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(initAll, 50);
    });
  }
})();